import { useEffect, useState } from 'react';
import { Snackbar, Checkbox, FormControlLabel, Grid, Link } from '@mui/material';
import { acceptCookies, isAnalyticCookiesAccepted, isCookiesAccepted } from '../../helpers/cookiesConsentStorage';
import { useTranslation } from 'react-i18next';
import { CookieButton } from './cookieButton';
import { isDevEnvironment } from '../../env';
import { AppStyle } from '../../helpers/appStyle';
import { appStyleMode, CompanyName, IsGtagEnabled, SiteName } from '../../helpers/clientConfigs';
import { openRules } from '../policy/rulesHelper';
import { useRouteNavigator } from '../../routes/useRouteNavigator';

export function CookiesBar() {
  const routeNavigator = useRouteNavigator();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation(['cookies']);
  const [analyticCookieAccepted, setAnalyticCookieAccepted] = useState(true);
  const [marketingCookieAccepted, setMarketingCookieAccepted] = useState(true);

  const handleAnalyticsCookieChange = () => {
    setAnalyticCookieAccepted(!analyticCookieAccepted);
  };

  const handleMarketingCookieChange = () => {
    setMarketingCookieAccepted(!marketingCookieAccepted);
  };

  useEffect(() => {
    if (!isCookiesAccepted()) {
      setOpen(true);
    } else if (isAnalyticCookiesAccepted()) {
      updateGtagConsent(true);
    }
  }, []);

  const acceptCookiesPolicy = () => {
    acceptCookies(analyticCookieAccepted, marketingCookieAccepted);
    updateGtagConsent(analyticCookieAccepted);
    setOpen(false);
  };

  const acceptAllCookiesPolicy = () => {
    acceptCookies(true, true);
    updateGtagConsent(true);
    setOpen(false);
  };

  const handleCookiesPolicyClick = () => {
    if (appStyleMode === AppStyle.Mobile) {
      openRules(routeNavigator.getRulesPath());
      return;
    }
    routeNavigator.goToRules();
  };

  const updateGtagConsent = (analyticCookiesAccepted: boolean) => {
    if (window.cordova) {
      window.cordova.plugins.firebase.analytics.setEnabled(analyticCookiesAccepted);
      return;
    }

    if (isDevEnvironment() || !IsGtagEnabled) {
      return;
    }

    const consent = {
      ad_storage: analyticCookiesAccepted ? 'granted' : 'denied',
      analytics_storage: analyticCookiesAccepted ? 'granted' : 'denied',
      ad_user_data: analyticCookiesAccepted ? 'granted' : 'denied',
      ad_personalization: analyticCookiesAccepted ? 'granted' : 'denied',
    } as Gtag.ConsentParams;
    gtag('consent', 'update', consent);
  };

  const getCookiesAction = () => {
    return (
      <Grid container direction="row" sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <Grid>
          <FormControlLabel
            control={<Checkbox defaultChecked disabled color="primary" />}
            label={t('cookies:necessary')}
          />
          <FormControlLabel
            control={
              <Checkbox checked={analyticCookieAccepted} onChange={handleAnalyticsCookieChange} color="primary" />
            }
            label={t('cookies:analytics')}
          />
          <FormControlLabel
            control={
              <Checkbox checked={marketingCookieAccepted} onChange={handleMarketingCookieChange} color="primary" />
            }
            label={t('cookies:marketing')}
          />
        </Grid>
        <Grid sx={{ mx: '5px' }}>
          <Link
            sx={{
              color: 'black',
              textDecorationColor: 'black',
              '&:hover': {
                cursor: 'pointer',
              },
            }}
            onClick={acceptCookiesPolicy}
          >
            {t('cookies:acceptSelected')}
          </Link>
        </Grid>
        <Grid>
          <CookieButton content={t('cookies:acceptAll')} onClick={acceptAllCookiesPolicy} />
          <CookieButton content={t('cookies:policy')} onClick={handleCookiesPolicyClick} />
        </Grid>
      </Grid>
    );
  };

  return (
    <Snackbar
      sx={{
        '@media (min-width: 600px)': { minWidth: '560px' },
        '& .MuiSnackbarContent-action': {
          margin: 0,
          width: '100%',
        },
        '& .MuiSnackbarContent-root': {
          backgroundColor: 'white',
        },
        whiteSpace: 'pre-line',
        textAlign: 'center',
      }}
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      message={
        appStyleMode === AppStyle.Mobile
          ? t('cookies:messageMobile', { companyName: CompanyName })
          : t('cookies:messageWebsite', { siteName: SiteName, companyName: CompanyName })
      }
      action={getCookiesAction()}
    />
  );
}
